    import React, { useEffect, useState } from "react";
    import {
        Modal,
        Form,
        InputNumber,
        TimePicker,
        Button,
        Table,
        Space,
        notification,
        Select,
    } from "antd";
    import {
        PlusOutlined,
        EditOutlined,
        DeleteOutlined,
        SaveOutlined,
    } from "@ant-design/icons";
    import dayjs from "dayjs";
    import customParseFormat from "dayjs/plugin/customParseFormat";
    import drEasyApi from "../../../Api/Customapi";
    import { useQueryClient } from "@tanstack/react-query";

    dayjs.extend(customParseFormat);
    const timeFormat = "HH:mm";
    const displayTimeFormat = "hh:mm A"; // for showing in the UI
    const sendTimeFormat = "HH:mm"; // for sending to the server

    // Patient limit type options
    const patientLimitTypeOptions = [
        { value: 0, label: "Per Hour" },
        { value: 1, label: "Per Slot" },
    ];

    const AvailabilityHoursModal = ({ visible, onCancel, workingDay, onSuccess }) => {
        const [form] = Form.useForm();
        const [hours, setHours] = useState([]);
        const [editingId, setEditingId] = useState(null);
        const [loading, setLoading] = useState(false);
        const [savingId, setSavingId] = useState(null); // Track which slot is being saved
        const [maxPatients, setMaxPatients] = useState(0); // Track max patients value
        const token = localStorage.getItem('docTkn') || sessionStorage.getItem('docTkn');
        const queryClient = useQueryClient();

        useEffect(() => {
            if (visible && workingDay) {
                setHours(workingDay.availabilityHours || []);
            } else {
                setHours([]);
                setEditingId(null);
                setMaxPatients(0);
                form.resetFields();
            }
        }, [visible, workingDay]);

        const validateTimeRange = (start, end) => {
            return dayjs(end, timeFormat).isAfter(dayjs(start, timeFormat));
        };

        // Helper function to update React Query cache
        const updateClinicsCache = (updateFn) => {
            queryClient.setQueryData(['clinics'], (oldData) => {
                if (!oldData?.clinics) return oldData;
                
                return {
                    ...oldData,
                    clinics: oldData.clinics.map(clinic => {
                        if (clinic.clinicId !== workingDay.clinicId) return clinic;
                        
                        return {
                            ...clinic,
                            doctors: clinic.doctors.map(doctor => {
                                if (doctor.doctorId !== workingDay.doctorId) return doctor;
                                
                                return {
                                    ...doctor,
                                    workingDays: doctor.workingDays.map(wd => {
                                        if (wd.workingDayId !== workingDay.id && wd.workingDayId !== workingDay.workingDayId) return wd;
                                        
                                        return updateFn(wd);
                                    })
                                };
                            })
                        };
                    })
                };
            });
        };

        const handleSaveIndividualSlot = async () => {
            try {
                const values = await form.validateFields();
                console.log('==============values======================');
                console.log(values);
                console.log('====================================');
                const slotData = {
                    startTime: values.startTime.format(sendTimeFormat),
                    endTime: values.endTime.format(sendTimeFormat),
                    maxPatients: values.maxPatients || 0,
                    maxPatientType: 0
                };

                console.log('Form values:', values);
                console.log('Slot data being sent:', slotData);

                if (!validateTimeRange(slotData.startTime, slotData.endTime)) {
                    notification.warning({
                        message: "Invalid Time Range",
                        description: "End time must be after start time.",
                    });
                    return;
                }

                setSavingId(editingId || 'new');

                if (editingId) {
                    // Update existing slot
                    await drEasyApi.put("/api/Clinic/EditAvailabilityHour", {
                        id: editingId,
                        startTime: slotData.startTime,
                        endTime: slotData.endTime,
                        maxPatients: slotData.maxPatients,
                        maxPatientType: slotData.maxPatientType,
                    }, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });

                    // Update local state
                    setHours((prev) =>
                        prev.map((h) => (h.id === editingId || h.availabilityHourId === editingId) ? { ...h, ...slotData } : h)
                    );

                    updateClinicsCache((workingDay) => ({
                        ...workingDay,
                        availabilityHours: workingDay.availabilityHours.map(hour => 
                            (hour.id === editingId || hour.availabilityHourId === editingId) 
                                ? { ...hour, ...slotData } 
                                : hour
                        )
                    }));

                    notification.success({
                        message: "Updated",
                        description: "Time slot updated successfully",
                    });
                    setEditingId(null);
                } else {
                    // Create new slot
                    const response = await drEasyApi.post("/api/Clinic/AddWorkingHours", {
                        workingDayId: workingDay.id,
                        hours: [slotData],
                    }, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });

                    // Add new slot to local state with the proper ID from server
                    const newSlot = {
                        // Use the proper ID structure from the server response
                        id: response.data?.addedHours[0]?.id || response.data?.id || response.data?.[0]?.id || `temp-${Date.now()}`,
                        availabilityHourId: response.data?.addedHours[0]?.id || response.data?.id || response.data?.[0]?.id || `temp-${Date.now()}`,
                        ...slotData,
                    };
                    setHours((prev) => [...prev, newSlot]);

                    // Update React Query cache immediately
                    updateClinicsCache((workingDay) => ({
                        ...workingDay,
                        availabilityHours: [...(workingDay.availabilityHours || []), newSlot]
                    }));

                    notification.success({
                        message: "Saved",
                        description: "Time slot saved successfully",
                    });
                }

                form.resetFields();
                setMaxPatients(0); // Reset max patients state
                
                // Call onSuccess callback to refresh parent data if needed
                if (onSuccess) {
                    onSuccess();
                }

            } catch (err) {
                notification.error({
                    message: editingId ? "Update failed" : "Save failed",
                    description: err.response?.data?.message || err.response?.data?.[0] || err.message,
                });
            } finally {
                setSavingId(null);
            }
        };

        const handleEdit = (hour) => {
            console.log(hour);
            
            setEditingId(hour.availabilityHourId || hour.id);
            setMaxPatients(hour.maxPatients || 0);
            form.setFieldsValue({
                startTime: dayjs(hour.startTime, sendTimeFormat),
                endTime: dayjs(hour.endTime, sendTimeFormat),
                maxPatients: hour.maxPatients,
                maxPatientType: hour.maxPatientType
            });
        };

        const handleCancelEdit = () => {
            setEditingId(null);
            setMaxPatients(0);
            form.resetFields();
        };

        const handleMaxPatientsChange = (value) => {
            setMaxPatients(value || 0);
            if (!value || value === 0) {
                // Reset limit type when max patients is cleared
                form.setFieldValue('maxPatientType', 1);
            }
        };

        const handleDelete = async (id) => {
            console.log(id);
            
            try {
                setLoading(true);
                
                // Check if this is a temporary ID (not saved to server yet)
                if (id && (id.toString().startsWith('new-') || id.toString().startsWith('temp-'))) {
                    // Just remove from local state, no server call needed
                    setHours((prev) => prev.filter((h) => h.id !== id));
                    
                    // Update React Query cache immediately
                    updateClinicsCache((workingDay) => ({
                        ...workingDay,
                        availabilityHours: workingDay.availabilityHours.filter(hour => hour.id !== id)
                    }));
                    
                    notification.success({
                        message: "Deleted",
                        description: "The time slot was removed successfully",
                    });

                    if (editingId === id) {
                        handleCancelEdit();
                    }

                    if (onSuccess) {
                        onSuccess();
                    }
                    return;
                }

                await drEasyApi.delete(`/api/Clinic/DeleteAvailabilityHour/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                
                // Update local state
                setHours((prev) => prev.filter((h) => h.id !== id && h.availabilityHourId !== id));
                
                updateClinicsCache((workingDay) => ({
                    ...workingDay,
                    availabilityHours: workingDay.availabilityHours.filter(hour => 
                        hour.id !== id && hour.availabilityHourId !== id
                    )
                }));
                
                notification.success({
                    message: "Deleted",
                    description: "The time slot was removed successfully",
                });

                if (editingId === id) {
                    handleCancelEdit();
                }

                // Call onSuccess callback to refresh parent data
                if (onSuccess) {
                    onSuccess();
                }

            } catch (err) {
                notification.error({
                    message: "Error deleting time slot",
                    description: err.response?.data?.message || err.message,
                });
            } finally {
                setLoading(false);
            }
        };

        const getPatientLimitTypeLabel = (type) => {
            const option = patientLimitTypeOptions.find(opt => opt.value === type);
            return option ? option.label : "Per Slot";
        };

        const columns = [
            {
                title: "Start Time",
                dataIndex: "startTime",
                render: (time) => dayjs(time, sendTimeFormat).format(displayTimeFormat),
            },
            {
                title: "End Time",
                dataIndex: "endTime",
                render: (time) => dayjs(time, sendTimeFormat).format(displayTimeFormat),
            },
            {
                title: "Max Patients (Per Hour)",
                dataIndex: "maxPatients",
                render: (val) => (val ? val : "No limit"),
            },
            // {
            //     title: "Limit Type",
            //     dataIndex: "maxPatientType",
            //     render: (type, record) => record.maxPatients > 0 ? getPatientLimitTypeLabel(type) : "-",
            // },
            {
                title: "Actions",
                render: (_, record) => (
                    <Space>
                        <Button 
                            icon={<EditOutlined />} 
                            onClick={() => handleEdit(record)}
                            disabled={editingId === record.id}
                        />
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(record.availabilityHourId || record.id)}
                            loading={loading}
                            disabled={editingId === record.id}
                        />
                    </Space>
                ),
            },
        ];

        return (
            <Modal
                title={`Manage Availability - ${workingDay?.clinicName} ${workingDay && `(${dayMap[workingDay.dayOfWeek]})`}`}
                open={visible}
                onCancel={() => {
                    handleCancelEdit();
                    onCancel();
                }}
                width={900}
                footer={[
                    <Button key="cancel" onClick={() => {
                        handleCancelEdit();
                        onCancel();
                    }}>
                        Close
                    </Button>,
                ]}
            >
                <div>
                    <Form layout="vertical" form={form}>
                        {/* Time fields row */}
                        <div className="flex gap-4">
                            <Form.Item
                                name="startTime"
                                label="Start Time"
                                rules={[{ required: true, message: "Start time is required" }]}
                                className="flex-1"
                            >
                                <TimePicker className="w-full" format={displayTimeFormat} />
                            </Form.Item>

                            <Form.Item
                                name="endTime"
                                label="End Time"
                                rules={[{ required: true, message: "End time is required" }]}
                                className="flex-1"
                            >
                                <TimePicker className="w-full" format={displayTimeFormat} />
                            </Form.Item>
                        </div>

                        {/* Patient settings row */}
                        <div className="flex gap-4">
                            <Form.Item name="maxPatients" label="Max Patients (Per Hour)" className="flex-1">
                                <InputNumber 
                                    min={0} 
                                    placeholder="No limit" 
                                    className="w-full" 
                                    onChange={handleMaxPatientsChange}
                                />
                            </Form.Item>

                        
                        </div>

                        <Form.Item>
                            <Space>
                                <Button className="bg-primary"
                                    onClick={handleSaveIndividualSlot}
                                    type="primary"
                                    loading={savingId === (editingId || 'new')}
                                >
                                    {editingId ? "Update Slot" : "Save Slot"}
                                </Button>
                                {editingId && (
                                    <Button onClick={handleCancelEdit}>
                                        Cancel Edit
                                    </Button>
                                )}
                            </Space>
                        </Form.Item>
                    </Form>

                    <Table
                        columns={columns}
                        dataSource={hours}
                        rowKey="id"
                        pagination={false}
                        size="small"
                        locale={{
                            emptyText: "No time slots configured. Add one above."
                        }}
                    />
                </div>
            </Modal>
        );
    };

    const dayMap = {
        0: "Sunday",
        1: "Monday",
        2: "Tuesday",
        3: "Wednesday",
        4: "Thursday",
        5: "Friday",
        6: "Saturday",
    };

    export default AvailabilityHoursModal;